import { FC } from "react"
import { FaToggleOff, FaToggleOn, FaTrash } from "react-icons/fa"

import { ActionType, ConditionType, ModifierType, PointRule } from "types/tournament.types"

import { ACTION_OPTIONS, CONDITION_OPTIONS, groupedOptions, MODIFIER_OPTIONS } from "../constants"

import { ActionFields } from "./ActionFields"
import { ConditionFields } from "./ConditionFields"
import { NumberInput } from "./NumberInput"

interface RuleEditorProps {
  rule: PointRule
  onUpdate: (rule: PointRule) => void
  onRemove: () => void
  onToggle: () => void
}

export const RuleEditor: FC<RuleEditorProps> = ({ rule, onUpdate, onRemove, onToggle }) => {
  const updateCondition = (key: string, val: any) => {
    onUpdate({ ...rule, if: { ...rule.if, [key]: val } })
  }

  const updateAction = (key: string, val: any) => {
    onUpdate({ ...rule, then: { ...rule.then, [key]: val } })
  }

  const updateModifier = (key: string, val: any) => {
    const mod = rule.modifier || { type: "no_limit" as ModifierType }
    onUpdate({ ...rule, modifier: { ...mod, [key]: val } })
  }

  const setConditionType = (type: ConditionType) => {
    onUpdate({ ...rule, if: { type } })
  }

  const setActionType = (type: ActionType) => {
    onUpdate({ ...rule, then: { type } })
  }

  const setModifierType = (type: ModifierType) => {
    onUpdate({ ...rule, modifier: { type } })
  }

  const isDisabled = rule.enabled === false

  return (
    <div
      className={`p-4 border rounded-lg space-y-3 ${
        isDisabled ? "opacity-50 border-gray-200 dark:border-gray-800" : "border-gray-300 dark:border-gray-700"
      }`}
    >
      {/* Rule header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <button type="button" onClick={onToggle} className="text-lg" title={rule.enabled ? "Disable rule" : "Enable rule"}>
            {rule.enabled !== false ? <FaToggleOn className="text-green-500" /> : <FaToggleOff className="text-gray-400" />}
          </button>
          <input
            type="text"
            value={rule.name}
            onChange={(e) => onUpdate({ ...rule, name: e.target.value })}
            className="flex-1 px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white font-medium"
            placeholder="Rule name"
          />
        </div>
        <button type="button" onClick={onRemove} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
          <FaTrash />
        </button>
      </div>

      {/* IF condition */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">IF (When):</label>
        <select
          value={rule.if.type}
          onChange={(e) => setConditionType(e.target.value as ConditionType)}
          className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        >
          {groupedOptions(CONDITION_OPTIONS)}
        </select>
        <ConditionFields condition={rule.if} onChange={updateCondition} />
      </div>

      {/* THEN action */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">THEN (Do):</label>
        <select
          value={rule.then.type}
          onChange={(e) => setActionType(e.target.value as ActionType)}
          className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        >
          {groupedOptions(ACTION_OPTIONS)}
        </select>
        <ActionFields action={rule.then} onChange={updateAction} />
      </div>

      {/* MODIFIER */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Limit:</label>
        <select
          value={rule.modifier?.type || "no_limit"}
          onChange={(e) => setModifierType(e.target.value as ModifierType)}
          className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        >
          {MODIFIER_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        {["max_times", "max_per_hour", "max_per_day"].includes(rule.modifier?.type || "") && (
          <NumberInput label="Max count" value={rule.modifier?.count} onChange={(v) => updateModifier("count", v)} placeholder="e.g., 5" />
        )}
      </div>
    </div>
  )
}
